const fallbackPreferenceKey = 'apiFallbackPreferredUntil';
const fallbackPreferenceMs = 60 * 60 * 1000;
const apiServerModeKey = 'apiServerMode';
export const apiServerModeChangeEvent = 'api-server-mode-change';

export type ApiServerMode = 'auto' | 'hongKong';

export const primaryApiBasePath = import.meta.env.VITE_API_BASE_PATH;
export const fallbackApiBasePath = import.meta.env.VITE_API_FALLBACK_BASE_PATH;

export const hasFallbackApiBasePath = import.meta.env.PROD && Boolean(fallbackApiBasePath);
export const shouldShowApiServerSwitch = import.meta.env.DEV || hasFallbackApiBasePath;

export const preferFallbackApi = () => {
  if (!hasFallbackApiBasePath) return;
  localStorage.setItem(fallbackPreferenceKey, String(Date.now() + fallbackPreferenceMs));
};

export const clearFallbackApiPreference = () => {
  localStorage.removeItem(fallbackPreferenceKey);
};

export const getApiServerMode = (): ApiServerMode => {
  if (!hasFallbackApiBasePath) return 'auto';
  return localStorage.getItem(apiServerModeKey) === 'hongKong' ? 'hongKong' : 'auto';
};

export const setApiServerMode = (mode: ApiServerMode) => {
  if (!hasFallbackApiBasePath) {
    clearFallbackApiPreference();
    return 'auto';
  }

  localStorage.setItem(apiServerModeKey, mode);
  clearFallbackApiPreference();
  window.dispatchEvent(new CustomEvent(apiServerModeChangeEvent, { detail: mode }));
  return mode;
};

export const shouldForceFallbackApi = () => {
  return hasFallbackApiBasePath && getApiServerMode() === 'hongKong';
};

export const shouldPreferFallbackApi = () => {
  if (shouldForceFallbackApi()) return true;
  if (!hasFallbackApiBasePath) return false;

  const preferredUntil = Number(localStorage.getItem(fallbackPreferenceKey));
  if (!preferredUntil || Number.isNaN(preferredUntil)) return false;

  if (preferredUntil > Date.now()) return true;

  clearFallbackApiPreference();
  return false;
};

export const getApiBasePath = () => {
  return shouldPreferFallbackApi() ? fallbackApiBasePath : primaryApiBasePath;
};

export const buildApiUrl = (path: string) => {
  const basePath = getApiBasePath();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return `${basePath}${normalizedPath}`;
};

export const isNetworkError = (error: unknown) => {
  return error instanceof TypeError;
};

export const apiFetch = async (path: string, init?: RequestInit) => {
  try {
    return await fetch(buildApiUrl(path), init);
  } catch (error) {
    if (
      !isNetworkError(error) ||
      !hasFallbackApiBasePath ||
      shouldPreferFallbackApi() ||
      getApiServerMode() !== 'auto'
    ) {
      throw error;
    }

    preferFallbackApi();
    return fetch(buildApiUrl(path), init);
  }
};
